import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DropdownOption } from "./types";

interface Params<T extends string | number> {
  options: DropdownOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Max options rendered at once; extra matches are hidden behind a hint. */
  maxRendered?: number;
}

/**
 * Behavioural core of the searchable dropdown: open/close, text filtering,
 * keyboard navigation, focus management and outside-click dismissal. Kept
 * separate from the markup (single responsibility) so the presentation stays
 * declarative.
 */
export function useSearchableDropdown<T extends string | number>({
  options,
  value,
  onChange,
  disabled = false,
  maxRendered = 100,
}: Params<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.code ?? "").toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q) ||
        (o.keywords ?? "").toLowerCase().includes(q),
    );
  }, [options, query]);

  // Only a bounded slice is rendered/navigable; the rest sits behind a hint.
  const visible = useMemo(
    () => (filtered.length > maxRendered ? filtered.slice(0, maxRendered) : filtered),
    [filtered, maxRendered],
  );
  const truncatedCount = filtered.length - visible.length;

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    setQuery("");
    setHighlighted(0);
    // Return focus to the trigger on keyboard/selection dismissal so the user
    // keeps their place in the form. Outside-click leaves focus where it lands.
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    if (!disabled) setOpen(true);
  }, [disabled]);

  const toggle = useCallback(() => {
    if (disabled) return;
    if (open) close();
    else openMenu();
  }, [disabled, open, close, openMenu]);

  const select = useCallback(
    (opt: DropdownOption<T>) => {
      if (opt.disabled) return;
      onChange(opt.value);
      close(true);
    },
    [onChange, close],
  );

  // Dismiss when clicking outside the control.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);

  // Focus the search box as soon as the menu opens.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Keep the highlight index within the visible set as it shrinks.
  useEffect(() => {
    setHighlighted((h) => (h >= visible.length ? 0 : h));
  }, [visible.length]);

  // Keep the highlighted row visible while navigating by keyboard.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlighted] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlighted, open]);

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      openMenu();
    }
  };

  // Next selectable index in `dir`, skipping disabled options; stays put when there is none.
  const stepHighlight = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < visible.length; i += dir) {
      if (!visible[i].disabled) return i;
    }
    return from;
  };

  // Attached to the search input. Home/End are intentionally left to the
  // browser so they move the text caret rather than the option highlight.
  const onListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlighted((h) => stepHighlight(h, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlighted((h) => stepHighlight(h, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (visible[highlighted]) select(visible[highlighted]);
        break;
      case "Escape":
        e.preventDefault();
        close(true);
        break;
      case "Tab":
        close();
        break;
      default:
        break;
    }
  };

  const onSearchChange = (v: string) => {
    setQuery(v);
    setHighlighted(0);
  };

  return {
    open,
    query,
    highlighted,
    filtered,
    visible,
    truncatedCount,
    selected,
    containerRef,
    triggerRef,
    searchRef,
    listRef,
    toggle,
    close,
    select,
    setHighlighted,
    onTriggerKeyDown,
    onListKeyDown,
    onSearchChange,
  };
}
